import { useWindowWidth } from '@react-hook/window-size';
import type { Competition } from '@uefadigital/panenka';
import {
  PkBadge,
  PkEmptyMessage,
  PkIdentifier,
  PkTable,
  PkTableBody,
  PkTableCell,
  PkTableHeader,
  PkTableHeaderCol,
  PkTableRow,
} from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useGetDomesticStandingsQuery } from 'api/fspApi';
import useIsDesktop from 'hooks/useIsDesktop';
import type { TeamStanding } from 'types/ApiResponseInterfaces';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

interface DomesticStandingsTabData {
  competition: Competition | undefined;
  seasonYear: string;
  teamsId: Array<string>;
}

interface TableWidth {
  firstCell: number;
  restCells: number;
}

const desktopCriterias = ['played', 'won', 'drawn', 'lost', 'goalsFor', 'goalsAgainst', 'goalDifference', 'points'];

const midCriterias = ['played', 'won', 'drawn', 'lost', 'goalDifference', 'points'];
const mobileCriterias = ['played', 'goalDifference', 'points'];

const displayTableCell = (teamData: TeamStanding, isDesktop: boolean, criterias: Array<string>) =>
  Object.entries(teamData)
    .filter((el) => criterias.includes(el[0]))
    .sort((a, b) => criterias.indexOf(a[0]) - criterias.indexOf(b[0]))
    .map((teamStandingData, criteria, arr) => {
      const isBoldSpan = arr.length - 1 === criteria ? style.spanBold : '';

      return (
        <PkTableCell key={criteria} columnKey={teamStandingData[0]} aligned="center">
          <span className={` ${isDesktop ? 'pk-font-size--m' : 'pk-font-size--s'} ${isBoldSpan}`}>
            {String(teamStandingData[1])}
          </span>
        </PkTableCell>
      );
    });

const getCriterias = (width: number) => {
  if (width <= 610) return mobileCriterias;
  if (width <= 960) return midCriterias;
  return desktopCriterias;
};

const getCellsWidth = (width: number) => {
  const tableWidth: TableWidth = {
    firstCell: 28,
    restCells: 9,
  };

  if (width <= 960) {
    tableWidth.firstCell = 40;
    tableWidth.restCells = 10;
  }

  if (width <= 610) {
    tableWidth.firstCell = 55;
    tableWidth.restCells = 15;
  }

  return tableWidth;
};

const HeaderCells = ({
  criterias,
  cellsWidth,
  isDesktop,
}: {
  criterias: Array<string>;
  cellsWidth: TableWidth;
  isDesktop: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <>
      {criterias.map((criteria, i) => {
        const isBoldSpan = criterias.length - 1 === i ? style.spanBold : '';
        const criteriaSnakeCase = criteria
          .split(/(?=[A-Z])/)
          .join('_')
          .toLowerCase();

        return (
          <PkTableHeaderCol
            columnKey={criteria}
            key={criteria}
            width={cellsWidth.restCells}
            usePercentageWidth
            aligned="center"
          >
            <span className={`${isBoldSpan} pk-font-size--xs`}>
              {isDesktop ? t(criteriaSnakeCase) : t(`${criteriaSnakeCase}_small`)}
            </span>
          </PkTableHeaderCol>
        );
      })}
    </>
  );
};

export const DomesticStandingsTab = ({ competition, teamsId }: DomesticStandingsTabData) => {
  const windowWidth = useWindowWidth();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const {
    data: teamTournamentStandings,
    isLoading: teamTournamentStandingsLoading,
    isError: teamTournamentStandingsError,
  } = useGetDomesticStandingsQuery(
    {
      competitionId: competition?.id ?? '0',
    },
    {
      skip: !competition,
    },
  );

  if (teamTournamentStandingsLoading) {
    return <div />;
  }

  if (teamTournamentStandingsError) {
    return <PkEmptyMessage className="pr-font-size--14">{t('table_is_not_available')}</PkEmptyMessage>;
  }

  const teamStandings = teamTournamentStandings?.[0]?.items;
  const criterias = getCriterias(windowWidth);
  const cellsWidth = getCellsWidth(windowWidth);

  return (
    <PkTable cellVerticalPad={16} className="pk-table sc-pk-table-h sc-pk-table-s" rowSpacing={0} rowDivider>
      <PkTableHeader>
        <PkTableRow className={style.removeRowDivider}>
          <PkTableHeaderCol columnKey="competitions" width={cellsWidth.firstCell} usePercentageWidth canGrow />
          <HeaderCells criterias={criterias} cellsWidth={cellsWidth} isDesktop={isDesktop} />
        </PkTableRow>
      </PkTableHeader>
      <PkTableBody>
        {teamStandings?.map((teamData, i) => {
          const teamBg = teamsId.includes(teamData?.team?.id) ? 'ui-02' : undefined;
          const isTeamBorder = teamsId.includes(teamData?.team?.id) && isDesktop;
          const marginRight = teamData.rank < 10 ? 'pk-mr--l' : 'pk-mr--s';

          return (
            <PkTableRow
              key={i}
              bgColor={teamBg}
              cellColor="text-01"
              className={clsx('pk-mb--0 pk-shadow--none', isTeamBorder && 'pk-rounded--l')}
            >
              <PkTableCell>
                <PkIdentifier alignment="left">
                  <span slot="prefix">
                    <span className={`${marginRight} pk-font-size--m`}>{teamData?.rank} </span>
                    <PkBadge
                      alt="Team"
                      fallbackImage="club-generic-badge"
                      src={teamData?.team?.mediumLogoUrl}
                      class="hydrated"
                      size={isDesktop ? 32 : 24}
                    />
                  </span>
                  <span slot="primary" className="pk-text--text-01 pk-font-size--s">
                    {translateEntity(teamData?.team)?.displayName ?? t('team')}{' '}
                  </span>
                </PkIdentifier>
              </PkTableCell>
              {displayTableCell(teamData, isDesktop, criterias)}
            </PkTableRow>
          );
        })}
      </PkTableBody>
    </PkTable>
  );
};
